/**
 * Contact Master — UI redesign of /people (Sprint 7 data).
 * List view default with search, channel filter, sort, and pagination.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import {
  Search, RefreshCw, Download, MessageSquare, UsersRound, Loader2, ChevronLeft, ChevronRight,
} from "lucide-react";
import { peopleApi } from "@/lib/api";
import { useExclusiveAction } from "@/hooks/useExclusiveAction";
import { ContactStatsCards } from "@/components/people/ContactStatsCards";
import { ContactViewToggle } from "@/components/people/ContactViewToggle";
import { ContactCard } from "@/components/people/ContactCard";
import { ContactTable } from "@/components/people/ContactTable";
import { exportPeopleCsv } from "@/pages/people/contactMasterUtils";

const VIEW_KEY = "sellix.contactMaster.view";
const PAGE_SIZE_KEY = "sellix.contactMaster.pageSize";
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const CHANNEL_FILTERS = [
  { value: "all", label: "All" },
  { value: "email", label: "Email" },
  { value: "whatsapp", label: "WhatsApp" },
  { value: "telegram", label: "Telegram" },
];

function readStoredView() {
  try {
    const v = localStorage.getItem(VIEW_KEY);
    return v === "list" || v === "grid" ? v : "list";
  } catch {
    return "list";
  }
}

function readStoredPageSize() {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_KEY));
    return PAGE_SIZE_OPTIONS.includes(n) ? n : 25;
  } catch {
    return 25;
  }
}

function emptyMessage({ totalAll, totalFiltered, q, channel }) {
  if (totalAll === 0) {
    return {
      title: "No people yet",
      body: "Once a real conversation lands in your inbox, the other party will show up here automatically.",
      showInbox: true,
    };
  }
  if (q.trim() && channel !== "all") {
    return {
      title: "No matches",
      body: `No contacts match “${q.trim()}” with the ${channel} filter.`,
      showInbox: false,
    };
  }
  if (q.trim()) {
    return {
      title: "No matches",
      body: "Try a different search term.",
      showInbox: false,
    };
  }
  if (channel !== "all") {
    const channelDesc = channel === "email" ? "an email address" : `a ${channel} identity`;
    return {
      title: "No contacts for this channel",
      body: `No contacts with ${channelDesc} yet.`,
      showInbox: false,
    };
  }
  return {
    title: "No matches",
    body: "Try adjusting your filters.",
    showInbox: false,
  };
}

export default function BackendPeoplePage() {
  const nav = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [qInput, setQInput] = useState("");
  const [q, setQ] = useState("");
  const [channel, setChannel] = useState("all");
  const [sortKey, setSortKey] = useState("last_touch");
  const [sortOrder, setSortOrder] = useState("desc");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(readStoredPageSize);
  const [view, setView] = useState(readStoredView);
  const [inactiveIds, setInactiveIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await peopleApi.list({
        limit: pageSize,
        offset: page * pageSize,
        q: q || undefined,
        channel,
        sort: sortKey,
        order: sortOrder,
      });
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, error: e.message || "Failed to load", data: null });
    }
  }, [page, pageSize, q, channel, sortKey, sortOrder]);

  useEffect(() => { queueMicrotask(load); }, [load]);

  // Debounce search input → committed query; reset page.
  useEffect(() => {
    const t = window.setTimeout(() => {
      const next = qInput.trim();
      setQ((prev) => {
        if (prev === next) return prev;
        setPage(0);
        return next;
      });
    }, 300);
    return () => window.clearTimeout(t);
  }, [qInput]);

  const onViewChange = useCallback((next) => {
    setView(next);
    try { localStorage.setItem(VIEW_KEY, next); } catch { /* ignore */ }
  }, []);

  const onPageSizeChange = useCallback((next) => {
    const n = Number(next);
    setPageSize(n);
    setPage(0);
    try { localStorage.setItem(PAGE_SIZE_KEY, String(n)); } catch { /* ignore */ }
  }, []);

  const onChannelChange = useCallback((next) => {
    setChannel(next);
    setPage(0);
  }, []);

  const onSortChange = useCallback((key, order) => {
    setSortKey(key);
    setSortOrder(order);
    setPage(0);
  }, []);

  const people = useMemo(() => state.data?.people || [], [state.data?.people]);
  const totalFiltered = state.data?.total ?? 0;
  const stats = state.data?.stats || {
    totalContacts: totalFiltered,
    channelCount: 0,
    conversations: 0,
    activeToday: 0,
  };
  const pageCount = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const from = totalFiltered === 0 ? 0 : page * pageSize + 1;
  const to = Math.min(totalFiltered, page * pageSize + people.length);

  const empty = useMemo(
    () => emptyMessage({
      totalAll: stats.totalContacts,
      totalFiltered,
      q,
      channel,
    }),
    [stats.totalContacts, totalFiltered, q, channel],
  );

  const openHistory = useCallback((person) => {
    if (person?.primary_conversation_id) {
      nav(`/inbox/${person.primary_conversation_id}`);
      return;
    }
    toast.info("No conversation history yet for this contact");
  }, [nav]);

  const onEdit = useCallback(() => {
    toast.info("Contact editing will be available in a later release");
  }, []);

  const onToggleInactive = useCallback((person) => {
    setInactiveIds((prev) => {
      const next = new Set(prev);
      if (next.has(person.id)) {
        next.delete(person.id);
        toast.success("Marked as active");
      } else {
        next.add(person.id);
        toast.success("Marked as inactive");
      }
      return next;
    });
  }, []);

  const onDelete = useCallback(() => {
    toast.info("Contact deletion is not available yet");
  }, []);

  const runExport = useCallback(() => {
    if (!people.length) {
      toast.error("Nothing to export");
      return null;
    }
    return exportPeopleCsv(people);
  }, [people]);

  const onDuplicateExport = useCallback(() => {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.info("[people-export] duplicate click ignored");
    }
  }, []);

  const [onExport, exporting] = useExclusiveAction(runExport, {
    cooldownMs: 600,
    onDuplicate: onDuplicateExport,
  });

  const handleExportClick = useCallback(async () => {
    const outcome = await onExport();
    if (outcome?.ignored) return;
    if (outcome?.error) {
      toast.error(outcome.error?.message || "Export failed");
      return;
    }
    const result = outcome?.result;
    if (!result) return;
    if (result.ignored) return;
    if (result.downloaded) {
      toast.success(`Exported ${result.rowCount} contact${result.rowCount === 1 ? "" : "s"}`);
    }
  }, [onExport]);

  return (
    <div
      className="flex-1 min-w-0 overflow-y-auto overflow-x-hidden scrollbar-thin bg-slate-50/40"
      data-testid="people-page"
    >
      <div className="mx-auto max-w-[1120px] w-full min-w-0 px-4 sm:px-6 md:px-8 py-6 sm:py-8 space-y-5 sm:space-y-6">

        <header className="flex flex-col gap-3 sm:gap-4 sm:flex-row sm:items-start sm:justify-between min-w-0">
          <div className="min-w-0">
            <div className="text-[11px] font-medium uppercase tracking-wide text-slate-400">
              Contact Master
            </div>
            <h1 className="mt-1 text-[24px] sm:text-[28px] md:text-[32px] font-semibold tracking-tight text-slate-900">
              Contact Master
            </h1>
            <p className="mt-1.5 text-[13px] sm:text-[13.5px] text-slate-500 max-w-[640px] leading-relaxed">
              Every person you&apos;ve had a real conversation with, unified across every connected
              channel — one record per person.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0 w-full sm:w-auto">
            <Button
              variant="outline"
              size="sm"
              className="h-10 sm:h-8 flex-1 sm:flex-none text-[12px] border-slate-200 text-slate-700"
              onClick={handleExportClick}
              disabled={state.loading || exporting || !people.length}
              aria-busy={exporting}
              data-testid="people-export"
            >
              {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              {exporting ? "Exporting…" : "Export"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="h-10 sm:h-8 flex-1 sm:flex-none text-[12px] border-slate-200 text-slate-700"
              onClick={load}
              disabled={state.loading || exporting}
              data-testid="people-refresh"
            >
              <RefreshCw size={13} className={cn(state.loading && "animate-spin")} />
              Refresh
            </Button>
          </div>
        </header>

        <ContactStatsCards
          totalContacts={stats.totalContacts}
          channelCount={stats.channelCount}
          conversations={stats.conversations}
          activeToday={stats.activeToday}
        />

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center min-w-0">
          <div className="relative flex-1 min-w-0">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <Input
              value={qInput}
              onChange={(e) => setQInput(e.target.value)}
              placeholder="Search by name, phone, or email..."
              className="h-11 sm:h-10 rounded-full border-slate-200 bg-white pl-10 text-[13px] shadow-none"
              data-testid="people-search"
            />
          </div>
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 flex-wrap justify-between sm:justify-end min-w-0">
            <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
              <span className="sr-only sm:not-sr-only">Filter</span>
              <select
                value={channel}
                onChange={(e) => onChannelChange(e.target.value)}
                className="h-10 sm:h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-[12.5px] text-slate-700"
                data-testid="people-channel-filter"
              >
                {CHANNEL_FILTERS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </label>
            <label className="inline-flex items-center gap-1.5 text-[12px] text-slate-500">
              <span className="hidden sm:inline">Per page</span>
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(e.target.value)}
                className="h-10 sm:h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-[12.5px] text-slate-700"
                data-testid="people-page-size"
              >
                {PAGE_SIZE_OPTIONS.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </label>
            {state.data && (
              <span className="text-[12px] text-slate-500 whitespace-nowrap" data-testid="people-count">
                {from}–{to} of {totalFiltered}
              </span>
            )}
            <ContactViewToggle view={view} onChange={onViewChange} />
          </div>
        </div>

        <div className="min-w-0" data-testid="people-list">
          {state.loading && (
            view === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
                {["people-sk-a", "people-sk-b", "people-sk-c", "people-sk-d", "people-sk-e", "people-sk-f"].map((key) => (
                  <Skeleton key={key} className="h-[240px] sm:h-[260px] rounded-xl" />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                {["people-row-a", "people-row-b", "people-row-c", "people-row-d", "people-row-e", "people-row-f"].map((key) => (
                  <Skeleton key={key} className="h-16 sm:h-14 rounded-xl" />
                ))}
              </div>
            )
          )}

          {!state.loading && state.error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[12.5px] text-rose-700 break-words">
              Couldn&apos;t load people: {state.error}
            </div>
          )}

          {!state.loading && !state.error && people.length === 0 && (
            <div
              className="rounded-xl border border-dashed border-slate-200 bg-white px-4 sm:px-5 py-10 sm:py-12 text-center"
              data-testid="people-empty"
            >
              <UsersRound size={22} className="mx-auto text-slate-400" />
              <div className="mt-2 text-[13.5px] font-semibold text-slate-900">
                {empty.title}
              </div>
              <p className="mt-1 text-[12px] text-slate-500 max-w-[360px] mx-auto leading-relaxed px-1">
                {empty.body}
              </p>
              {empty.showInbox && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-10 sm:h-8 text-[12px] sm:text-[11.5px] mt-4"
                  onClick={() => nav("/inbox")}
                >
                  <MessageSquare size={11} /> Go to inbox
                </Button>
              )}
            </div>
          )}

          {!state.loading && !state.error && people.length > 0 && view === "grid" && (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 items-stretch">
              {people.map((p) => (
                <ContactCard
                  key={p.id}
                  person={p}
                  inactiveIds={inactiveIds}
                  onViewHistory={openHistory}
                  onEdit={onEdit}
                  onToggleInactive={onToggleInactive}
                  onDelete={onDelete}
                />
              ))}
            </div>
          )}

          {!state.loading && !state.error && people.length > 0 && view === "list" && (
            <ContactTable
              people={people}
              inactiveIds={inactiveIds}
              sortKey={sortKey}
              sortOrder={sortOrder}
              onSortChange={onSortChange}
              onViewHistory={openHistory}
              onEdit={onEdit}
              onToggleInactive={onToggleInactive}
              onDelete={onDelete}
            />
          )}

          {!state.loading && !state.error && totalFiltered > 0 && (
            <div
              className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2"
              data-testid="people-pagination"
            >
              <p className="text-[12.5px] text-slate-500 m-0">
                Page {page + 1} of {pageCount}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 sm:h-8 flex-1 sm:flex-none"
                  disabled={page <= 0 || state.loading}
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  data-testid="people-page-prev"
                >
                  <ChevronLeft size={14} /> Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-10 sm:h-8 flex-1 sm:flex-none"
                  disabled={state.loading || (page + 1) >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                  data-testid="people-page-next"
                >
                  Next <ChevronRight size={14} />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
